import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapPin, Clock, ChevronRight, CheckCircle2, Navigation,
  Calendar, Layers, AlertCircle, Loader2, Phone, MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useTasks } from '../context/TasksContext';
import { useLeads } from '../context/LeadsContext';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';
import WorkOrderSheet from '../features/clients/components/profile/WorkOrderSheet';
import ConfirmCompleteDialog from '../components/jobs/ConfirmCompleteDialog';
import FlashPill from '../components/propertyDetails/FlashPill';
import OverrideChip from '../components/overrides/OverrideChip';
import { resolveDisplayedStep } from '../utils/resolveDisplayedStep';
import MobileConnectivityBanner from '../components/mobile/MobileConnectivityBanner';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ────────────────────────────────────────────────────────────────────

// Returns "YYYY-MM-DD" for a given Date object in the supplied IANA timezone.
// Falling back to the device's local timezone means a tech in their home TZ
// sees zero behavioral change — the fix only activates when the tenant has
// configured a company-level timezone and the tech's device TZ differs.
function toDateKeyInTz(d, tz) {
  try {
    // en-CA gives the ISO "YYYY-MM-DD" layout directly, avoiding string parsing.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}

function formatTimeInTz(iso, tz) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d).replace(':00 ', ' '); // "9:00 AM" → "9 AM" for cleaner display
  } catch { return ''; }
}

function getAddress(lead) {
  if (!lead) return null;
  if (lead.addressDisplay) return lead.addressDisplay;
  const c = lead.contact || {};
  const parts = [c.addressLine1 || lead.addressLine1, c.city || lead.city, c.state || lead.state];
  const joined = parts.filter(Boolean).join(', ');
  if (joined) return joined;
  const a = lead.address;
  if (!a) return null;
  if (typeof a === 'string') return a;
  if (typeof a === 'object') return [a.line1, a.city, a.state].filter(Boolean).join(', ');
  return null;
}

// Normalize a customer phone to `tel:` / `sms:`-safe digits. The onboarding
// pipeline already stores 10-digit US numbers, but leads created before
// normalization existed may still hold formatted strings — strip everything
// non-numeric and require ≥10 digits before showing the one-tap actions.
function getPhoneDigits(lead) {
  const raw = lead?.phone || lead?.contact?.phone || '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < 10) return null;
  return digits;
}

const STATUS_CFG = {
  approved:  { label: 'Ready',     dot: 'bg-slate-400',    text: 'text-slate-600',    bg: 'bg-slate-100'   },
  scheduled: { label: 'Scheduled', dot: 'bg-blue-400',     text: 'text-blue-700',     bg: 'bg-blue-50'     },
  en_route:  { label: 'En Route',  dot: 'bg-amber-500',    text: 'text-amber-700',    bg: 'bg-amber-50'    },
  on_site:   { label: 'On Site',   dot: 'bg-indigo-500',   text: 'text-indigo-700',   bg: 'bg-indigo-50'   },
  completed: { label: 'Complete',  dot: 'bg-emerald-500',  text: 'text-emerald-700',  bg: 'bg-emerald-50'  },
};

const NEXT_ACTION = {
  approved:  { label: 'En Route',  nextStatus: 'en_route',  cls: 'bg-amber-500 text-white'   },
  scheduled: { label: 'En Route',  nextStatus: 'en_route',  cls: 'bg-amber-500 text-white'   },
  en_route:  { label: 'On Site',   nextStatus: 'on_site',   cls: 'bg-indigo-600 text-white'  },
  on_site:   { label: 'Complete',  nextStatus: 'completed', cls: 'bg-emerald-600 text-white', isComplete: true },
};

// ── En-route SMS helper ────────────────────────────────────────────────────────

async function sendEnRouteSms(companyId, taskId) {
  try {
    const token = await getAuth().currentUser?.getIdToken();
    let techLat = null;
    let techLng = null;
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
      );
      techLat = pos.coords.latitude;
      techLng = pos.coords.longitude;
    } catch { /* denied or unavailable — ETA falls back to "shortly" */ }

    await axios.post(
      `${API}/api/notifications/visit-reminders/en-route`,
      { companyId, taskId, techLat, techLng },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    toast.success('Customer notified — on the way!');
  } catch (err) {
    console.warn('[TodayMobile] En-route SMS (non-blocking):', err?.response?.data || err.message);
  }
}

// ── Job Card ───────────────────────────────────────────────────────────────────

function JobCard({ job, lead, onOpenDetail, onStatusAction, onUndoOnSite, acting, stepNumber, tz }) {
  const cfg = STATUS_CFG[job.status] || STATUS_CFG.approved;
  const nextAction = NEXT_ACTION[job.status];
  const addr = getAddress(lead);
  const phoneDigits = getPhoneDigits(lead);
  const telHref = phoneDigits ? `tel:+1${phoneDigits.slice(-10)}` : null;
  const smsHref = phoneDigits ? `sms:+1${phoneDigits.slice(-10)}` : null;
  const mapsUrl = addr ? `https://maps.google.com/?q=${encodeURIComponent(addr)}` : null;
  const { displayed: programStepDisplay, hasOverride: stepHasOverride } = resolveDisplayedStep(job);
  const programStep = programStepDisplay;
  const programName = job.programName;
  const isAppointment = job.jobMode === 'appointment';

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
      data-testid={`mobile-job-card-${job.id}`}
    >
      {/* Status accent bar */}
      <div className={`h-1 ${cfg.dot}`} />

      <div className="p-4 space-y-3">
        {/* Flash note — bright pill at top of every card incl. completed jobs */}
        {lead?.propertyDetails?.flashNote && (
          <FlashPill
            propertyDetails={lead.propertyDetails}
            size="sm"
            testId={`mobile-card-flash-${job.id}`}
            className="max-w-full"
          />
        )}

        {/* Override chip — show on upcoming visit cards */}
        {lead?.recurrence?.nextServiceOverride && (
          <OverrideChip
            override={lead.recurrence.nextServiceOverride}
            recurringAmount={lead.recurrence.amount}
            taskId={job.id}
            matchesEarliest={true}
            testId={`mobile-card-override-${job.id}`}
            className="max-w-full"
          />
        )}

        {/* Client name + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0 flex items-start gap-2">
            {/* Step number — matches dispatcher's route order */}
            {stepNumber != null && (
              <span
                className="shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold flex items-center justify-center mt-0.5"
                data-testid={`step-number-${job.id}`}
                aria-label={`Stop ${stepNumber}`}
              >
                {stepNumber}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 text-lg leading-tight truncate">
                {job.clientName || lead?.name || 'Unknown Client'}
              </p>
              {programName && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Layers className="h-3 w-3 text-indigo-500 shrink-0" />
                  <p className="text-xs text-indigo-600 font-medium truncate">{programName}</p>
                  {programStep?.label && (
                    <span className="text-xs text-gray-400">· {programStep.label}</span>
                  )}
                  {stepHasOverride && (
                    <span
                      className="ml-1 text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1"
                      data-testid={`mobile-card-customized-${job.id}`}
                    >
                      Customized
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <span className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} inline-block`} />
            {cfg.label}
          </span>
        </div>

        {/* Time + Fixed-time indicator for appointment jobs */}
        <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
          <Clock className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="font-medium">{formatTimeInTz(job.scheduledStart, tz) || 'Time TBD'}</span>
          {job.scheduledEnd && (
            <span className="text-gray-400">→ {formatTimeInTz(job.scheduledEnd, tz)}</span>
          )}
          {isAppointment && (
            <span
              className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold"
              data-testid={`fixed-time-pill-${job.id}`}
              title="This job has a fixed appointment time"
            >
              <Clock className="h-3 w-3" />
              Fixed time
            </span>
          )}
        </div>

        {/* Address */}
        {addr && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
            {mapsUrl ? (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-indigo-600 underline underline-offset-2 truncate"
                onClick={(e) => e.stopPropagation()}
                data-testid={`maps-link-${job.id}`}
              >
                {addr}
              </a>
            ) : (
              <span className="text-sm text-gray-500 truncate">{addr}</span>
            )}
          </div>
        )}

        {/* One-tap customer contact — only rendered when a valid phone is on
            file. Uses native tel:/sms: URIs so mobile OSes launch the dialer
            or SMS composer directly. stopPropagation keeps the parent card
            action from firing on tap. */}
        {phoneDigits && (
          <div className="flex items-center gap-2" data-testid={`contact-row-${job.id}`}>
            <Phone className="h-4 w-4 text-gray-400 shrink-0" />
            <a
              href={telHref}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
              data-testid={`call-btn-${job.id}`}
              aria-label={`Call ${job.clientName || lead?.name || 'customer'}`}
            >
              <Phone className="h-4 w-4 text-emerald-600" />
              Call
            </a>
            <a
              href={smsHref}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
              data-testid={`sms-btn-${job.id}`}
              aria-label={`Text ${job.clientName || lead?.name || 'customer'}`}
            >
              <MessageSquare className="h-4 w-4 text-indigo-600" />
              Text
            </a>
          </div>
        )}

        {/* Visit-Specific Instructions — office-authored, override-only.
            Rendered visually distinct from baseline Tech Notes and always
            ABOVE them so the technician sees exceptions first. */}
        {typeof programStep?.visitInstructions === 'string' && programStep.visitInstructions.trim().length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`mobile-visit-instructions-${job.id}`}>
            <p className="text-xs font-semibold text-amber-800 mb-0.5 uppercase tracking-wide">Visit-Specific Instructions</p>
            <p className="text-xs text-amber-900 leading-relaxed whitespace-pre-wrap">{programStep.visitInstructions}</p>
          </div>
        )}

        {/* Tech notes from program step */}
        {programStep?.techNotes && (
          <div className="bg-indigo-50 rounded-lg px-3 py-2">
            <p className="text-xs font-semibold text-indigo-700 mb-0.5">Tech Notes</p>
            <p className="text-xs text-indigo-600 leading-relaxed">{programStep.techNotes}</p>
          </div>
        )}

        {/* Actions row */}
        <div className="flex items-center gap-2 pt-1">
          {/* Primary status action */}
          {nextAction && job.status !== 'completed' && (
            <button
              type="button"
              onClick={() => onStatusAction(job, nextAction)}
              disabled={acting === job.id}
              className={`flex-1 h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-95 ${nextAction.cls} disabled:opacity-60`}
              data-testid={`status-btn-${job.id}`}
            >
              {acting === job.id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <>
                    {nextAction.nextStatus === 'en_route' && <Navigation className="h-4 w-4" />}
                    {nextAction.isComplete && <CheckCircle2 className="h-4 w-4" />}
                    {nextAction.label}
                  </>
              }
            </button>
          )}

          {/* Details button */}
          <button
            type="button"
            onClick={() => onOpenDetail(job)}
            className="h-11 w-11 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 active:scale-95 transition-all"
            data-testid={`detail-btn-${job.id}`}
            aria-label="Job details"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Undo — revert accidental On Site back to En Route (canonical scope: on_site only) */}
        {job.status === 'on_site' && (
          <button
            type="button"
            onClick={() => onUndoOnSite(job)}
            disabled={acting === job.id}
            className="w-full h-9 rounded-xl text-xs font-medium text-amber-700 hover:bg-amber-50 flex items-center justify-center gap-1.5 transition-all active:scale-95 disabled:opacity-60"
            data-testid={`undo-on-site-btn-${job.id}`}
            title="Revert to En Route — use this if arrival was logged by mistake"
          >
            <Navigation className="h-3.5 w-3.5" />
            Undo — back to En Route
          </button>
        )}

        {/* Completed state */}
        {job.status === 'completed' && (
          <div className="flex items-center gap-2 text-emerald-600 pt-1">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-sm font-semibold">Complete</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

const TodayMobile = () => {
  const { tasks, updateTask, completeJob } = useTasks();
  const { leads, ensureLeadIds } = useLeads();

  // Backfill leads referenced by today's jobs but outside the leads window
  useEffect(() => {
    const known = new Set(leads.map((l) => l.id));
    const missing = [...new Set(tasks.map((t) => t.leadId).filter(Boolean))].filter((id) => !known.has(id));
    if (missing.length) ensureLeadIds(missing);
  }, [tasks, leads, ensureLeadIds]);
  const { user } = useAuth();
  const { companyId, effectiveTimezone, companyTimezone, deviceTimezone } = useCompany();

  const [selectedJob, setSelectedJob] = useState(null);
  const [acting, setActing]           = useState(null); // jobId being acted on
  const [confirmJob, setConfirmJob]   = useState(null); // job pending completion confirm

  // Anchor "today" to the company's configured timezone (fallback: device TZ).
  // This prevents a tech working around midnight — or a tech whose device is
  // in a different timezone from the company — from seeing the wrong day.
  const tz = effectiveTimezone;
  const todayKey = toDateKeyInTz(new Date(), tz);
  const tzMismatch = companyTimezone && deviceTimezone && companyTimezone !== deviceTimezone;

  // ── Filter to today's jobs assigned to current tech ───────────────────────
  //    Sort order: dispatcher's plan wins (dispatchOrder), falls back to
  //    scheduledStart when a job has no order yet — so the tech's list on
  //    mobile mirrors the Dispatch board exactly.
  const todaysJobs = useMemo(() => {
    if (!tasks || !user?.uid) return [];
    return tasks
      .filter((t) => {
        if (!t || t.type !== 'job' || !t.scheduledStart) return false;
        try {
          const d = new Date(t.scheduledStart);
          if (isNaN(d.getTime())) return false;
          if (toDateKeyInTz(d, tz) !== todayKey) return false;
          // Strict assignment-only: only show jobs explicitly assigned to this technician.
          // Unassigned jobs must not appear on any technician's device (double-dispatch risk).
          return t.assignedTo === user.uid;
        } catch { return false; }
      })
      .sort((a, b) => {
        const aOrd = a.dispatchOrder;
        const bOrd = b.dispatchOrder;
        if (aOrd != null && bOrd != null) return aOrd - bOrd;
        if (aOrd != null) return -1;
        if (bOrd != null) return 1;
        return new Date(a.scheduledStart) - new Date(b.scheduledStart);
      });
  }, [tasks, user?.uid, todayKey, tz]);

  const leadsById = useMemo(() => {
    const m = {};
    (leads || []).forEach((l) => { if (l?.id) m[l.id] = l; });
    return m;
  }, [leads]);

  // Live task for the Work Order Sheet — always render the freshest snapshot
  // from the Firestore listener, never a stale click-time copy.
  const liveSelectedJob = selectedJob
    ? (todaysJobs.find((j) => j.id === selectedJob.id) || selectedJob)
    : null;

  // ── Canonical completion — always via completeJob() (billing + recurrence) ─
  const performComplete = useCallback(async (job) => {
    setActing(job.id);
    try {
      const data = await completeJob(job.id, {
        quoteId: job.quoteId,
        originUrl: window.location.origin,
      });
      if (data?.billingWarning) {
        toast.warning(data.message || 'Job complete — invoice could not be created', {
          description: data.billingWarning === 'stripe_not_configured'
            ? 'Office will re-issue the invoice.'
            : 'Office has been alerted.',
          duration: 10000,
        });
      } else if (data?.autoCharged) {
        const amt = data.autoChargeAmount != null ? ` — $${parseFloat(data.autoChargeAmount).toFixed(2)} charged` : '';
        toast.success(`Job complete${amt}`);
      } else if (data?.smsSent) {
        toast.success('Job complete — payment link sent');
      } else {
        toast.success('Job complete');
      }
    } catch (err) {
      console.error('[TodayMobile] complete failed:', err);
      toast.error('Status update failed');
    } finally {
      setActing(null);
    }
  }, [completeJob]);

  // ── Status action handler ─────────────────────────────────────────────────
  const handleStatusAction = useCallback(async (job, action) => {
    if (acting) return;
    if (action.isComplete) {
      setConfirmJob(job); // completion safeguard — confirm before billing fires
      return;
    }
    setActing(job.id);
    try {
      await updateTask(job.id, { status: action.nextStatus });
      if (action.nextStatus === 'en_route') {
        sendEnRouteSms(companyId, job.id); // fire-and-forget
      }
    } catch (err) {
      console.error('[TodayMobile] status action failed:', err);
      toast.error('Status update failed');
    } finally {
      setActing(null);
    }
  }, [acting, companyId, updateTask]);

  // ── Undo On Site — revert accidental arrival back to En Route ─────────────
  const handleUndoOnSite = useCallback(async (job) => {
    if (acting || job.status !== 'on_site') return;
    setActing(job.id);
    try {
      await updateTask(job.id, { status: 'en_route', arrivedAt: null });
      toast.success('Reverted to En Route');
    } catch (err) {
      console.error('[TodayMobile] undo on-site failed:', err);
      toast.error('Failed to revert status');
    } finally {
      setActing(null);
    }
  }, [acting, updateTask]);

  // ── Render ────────────────────────────────────────────────────────────────
  const completedCount = todaysJobs.filter((j) => j.status === 'completed').length;
  const remaining = todaysJobs.length - completedCount;
  // "Today" label rendered in the company's timezone so it matches the filter
  // logic above — critical when the tech's device is in a different TZ.
  const today = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }).format(new Date());
    } catch {
      return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    }
  })();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="today-mobile-page">
      {/* Header */}
      <div
        className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}
      >
        <h1 className="text-xl font-bold text-gray-900">Today&apos;s Jobs</h1>
        <p className="text-sm text-gray-500 mt-0.5">{today}</p>
        {tzMismatch && (
          <p
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5"
            data-testid="today-tz-mismatch-chip"
            title={`Your device is in ${deviceTimezone}; viewing today in the company timezone.`}
          >
            <Clock className="h-3 w-3" />
            Viewing today in {companyTimezone}
          </p>
        )}
      </div>

      {/* Connectivity banner — hidden while online, red when offline, green
          for a brief reconnect flash. Sits just under the sticky header so
          the tech sees it before their job list scrolls. */}
      <MobileConnectivityBanner />

      {/* Summary strip */}
      <div className="px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-900">{todaysJobs.length} jobs</span>
        </div>
        {remaining > 0 && (
          <span className="text-sm text-amber-600 font-medium">{remaining} remaining</span>
        )}
        {completedCount > 0 && (
          <span className="text-sm text-emerald-600 font-medium">{completedCount} complete</span>
        )}
      </div>

      {/* Job list */}
      <div
        className="px-4 py-4 space-y-3"
        style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
      >
        {todaysJobs.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-gray-700 font-semibold text-lg">No jobs today</p>
            <p className="text-gray-400 text-sm mt-1">Check back tomorrow</p>
          </div>
        ) : (
          todaysJobs.map((job, idx) => (
            <JobCard
              key={job.id}
              job={job}
              lead={leadsById[job.leadId]}
              onOpenDetail={setSelectedJob}
              onStatusAction={handleStatusAction}
              onUndoOnSite={handleUndoOnSite}
              acting={acting}
              stepNumber={idx + 1}
              tz={tz}
            />
          ))
        )}
      </div>

      {/* Canonical Work Order detail — same component as Customer Profile / Dispatch */}
      {liveSelectedJob && (
        <WorkOrderSheet
          task={liveSelectedJob}
          client={leadsById[liveSelectedJob.leadId]}
          open={!!liveSelectedJob}
          onClose={() => setSelectedJob(null)}
          onCompleted={() => setSelectedJob(null)}
        />
      )}

      {/* Completion safeguard */}
      <ConfirmCompleteDialog
        open={!!confirmJob}
        onOpenChange={(o) => { if (!o) setConfirmJob(null); }}
        clientName={confirmJob?.clientName || leadsById[confirmJob?.leadId]?.name}
        taskId={confirmJob?.id}
        onConfirm={() => {
          const j = confirmJob;
          setConfirmJob(null);
          if (j) performComplete(j);
        }}
      />
    </div>
  );
};

export default TodayMobile;
